import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Zap, Gift, Share2, Clock, QrCode, Check, AlertCircle, UserPlus, Ticket, Megaphone, Undo2, Send } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { staffApi } from '../../services/api';
import { buildPublicUrl, getBackendUrl } from '../../services/origin';
import { useBranding } from '../../context/BrandingContext';
import NumPad from '../components/NumPad';
import TierBadge, { TIER_AVATAR_BG } from '../components/TierBadge';
import ConfirmModal from '../../shared/ConfirmModal';
import OfflineBanner from '../../components/OfflineBanner';
import { enqueueOfflineAction } from '../../utils/offlineQueue';
import { playBip } from '../../utils/bip';
import { formatRedemptionConditionsShort, isRedeemableNow } from '../../utils/redemptionConditions';

const TX_LABELS = { purchase: 'Acquisto', manual_bonus: 'Bonus', redeem: 'Riscatto', referral: 'Referral', welcome: 'Benvenuto', birthday: 'Compleanno', coupon_bonus: 'Coupon', token_purchase: 'Gettoni' };
const TIER_RANK = { bronze: 0, silver: 1, gold: 2, platinum: 3 };
const TIER_COLORS = { bronze: '#CD7F32', silver: '#C0C0C0', gold: '#FFD700', platinum: '#E5E4E2' };
const TIER_LABELS = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum' };

function getProgressToNextTier(lifetime, tierNames = {}) {
  const tn = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum', ...tierNames };
  if (lifetime >= 1000) return { progress: 100, nextTier: null, pointsNeeded: 0, current: 1000, target: 1000 };
  if (lifetime >= 500) return { progress: ((lifetime - 500) / 500) * 100, nextTier: tn.platinum, pointsNeeded: 1000 - lifetime, current: lifetime - 500, target: 500 };
  if (lifetime >= 200) return { progress: ((lifetime - 200) / 300) * 100, nextTier: tn.gold, pointsNeeded: 500 - lifetime, current: lifetime - 200, target: 300 };
  return { progress: (lifetime / 200) * 100, nextTier: tn.silver, pointsNeeded: 200 - lifetime, current: lifetime, target: 200 };
}

const TABS = [
  { id: 'punti', label: 'Punti', icon: Zap },
  { id: 'premi', label: 'Premi', icon: Gift },
  { id: 'coupon', label: 'Coupon', icon: Ticket },
  { id: 'campagna', label: 'Campagna', icon: Megaphone },
  { id: 'referral', label: 'Referral', icon: Share2 },
  { id: 'storico', label: 'Storico', icon: Clock },
  { id: 'qr', label: 'QR', icon: QrCode },
];

export default function CustomerCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { primary_color, secondary_color } = useBranding();
  const [customer, setCustomer] = useState(null);
  const [settings, setSettings] = useState({ punti_per_euro: 1 });
  const [rewards, setRewards] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [tierBenefits, setTierBenefits] = useState([]);
  const [benefitClaims, setBenefitClaims] = useState({});
  const [creditedRewards, setCreditedRewards] = useState([]);
  const [activeTab, setActiveTab] = useState('punti');
  const [loading, setLoading] = useState(true);
  const [amountInput, setAmountInput] = useState('');
  const [confirm, setConfirm] = useState({ open: false, title: '', summary: '', newBalance: null, onConfirm: null, isLoading: false });
  const [refForm, setRefForm] = useState({ name: '', phone: '', email: '', birthday: '' });
  const [refLoading, setRefLoading] = useState(false);
  const [expiryInfo, setExpiryInfo] = useState(null);
  const [onboardingUrl, setOnboardingUrl] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [tokenSettings, setTokenSettings] = useState(null);
  const [tokenQty, setTokenQty] = useState(1);
  const [campaignPrizes, setCampaignPrizes] = useState([]);
  const [campaignPrizesLoaded, setCampaignPrizesLoaded] = useState(false);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [showAssignCampaignModal, setShowAssignCampaignModal] = useState(false);
  const [assigningCampaign, setAssigningCampaign] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Tick every second so the consent token countdown stays fresh.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [custRes, settingsRes] = await Promise.all([
        staffApi.get(`/customers/${id}`),
        staffApi.get('/business/settings'),
      ]);
      setCustomer(custRes.data);
      setSettings(settingsRes.data.settings || { punti_per_euro: 1 });
      // If the customer is mid-onboarding, derive the consent URL from the
      // token already on the record so the QR shown in the operator card
      // is never empty (operator may open the card before clicking
      // "Avvia onboarding"). If the token has expired the operator can
      // still hit the "Rigenera" button to get a fresh one.
      const c = custRes.data;
      if (c?.status === 'pending_consent' && c?.consent_token) {
        setOnboardingUrl(buildPublicUrl(`/consent/${c.consent_token}`));
      }
      staffApi.get(`/customers/${id}/expiring-points`).then(r => setExpiryInfo(r.data)).catch(() => {});
      staffApi.get('/admin/tokens/settings').then(r => setTokenSettings(r.data)).catch(() => {});
    } catch { toast.error('Errore nel caricamento del cliente'); navigate(-1); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll the consent status while the customer is mid-onboarding so the
  // operator sees the card flip to "completed" the moment the customer
  // finishes the privacy flow on their phone — and we can fire an audio
  // cue (BIP) to signal it.
  useEffect(() => {
    if (!customer || customer.status !== 'pending_consent') return;
    const t = setInterval(async () => {
      try {
        const r = await staffApi.get(`/customers/${id}/consent-status`);
        if (r.data?.status === 'active' && r.data?.gdpr_consent) {
          clearInterval(t);
          playBip();
          toast.success('Consenso ricevuto!');
          refresh();
        }
      } catch { /* ignore polling errors */ }
    }, 3000);
    return () => clearInterval(t);
  }, [customer, id, refresh]);

  useEffect(() => {
    if (activeTab === 'premi' && rewards.length === 0) {
      staffApi.get('/rewards').then(r => setRewards(r.data)).catch(() => {});
      staffApi.get('/tier-benefits').then(r => setTierBenefits(r.data)).catch(() => {});
      staffApi.get(`/tier-benefits/claims/${id}`).then(r => setBenefitClaims(r.data)).catch(() => {});
      staffApi.get(`/customers/${id}/credited-rewards`).then(r => setCreditedRewards(r.data)).catch(() => {});
    }
    if (activeTab === 'storico' && transactions.length === 0) {
      staffApi.get(`/customers/${id}/transactions`).then(r => setTransactions(r.data)).catch(() => {});
    }
    if (activeTab === 'referral' && referrals.length === 0) {
      staffApi.get(`/customers/${id}/referrals`).then(r => setReferrals(r.data)).catch(() => {});
    }
    if (activeTab === 'campagna' && !campaignPrizesLoaded) {
      staffApi.get(`/customers/${id}/campaign-prizes`).then(r => { setCampaignPrizes(r.data.prizes || []); setCampaignPrizesLoaded(true); }).catch(() => {});
      staffApi.get('/campaigns/active').then(r => setActiveCampaigns(r.data.campaigns || [])).catch(() => {});
    }
  }, [activeTab, id, rewards.length, transactions.length, referrals.length, campaignPrizesLoaded]);

  const tierNames = settings.tier_names || { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum' };
  const tierMultipliers = settings.tier_multipliers || { bronze: 1, silver: 1.25, gold: 1.5, platinum: 2 };

  const previewPoints = useMemo(() => {
    if (!amountInput || amountInput === '.') return 0;
    const amt = parseFloat(amountInput);
    if (isNaN(amt) || amt <= 0) return 0;
    return Math.floor(amt * (settings.punti_per_euro || 1) * (tierMultipliers[customer?.tier || 'bronze'] || 1));
  }, [amountInput, settings, customer?.tier, tierMultipliers]);

  const openConfirm = (title, summary, newBalance, onConfirm) => {
    setConfirm({ open: true, title, summary, newBalance, onConfirm, isLoading: false });
  };
  const closeConfirm = () => setConfirm(c => ({ ...c, open: false }));
  const runConfirm = async (fn, offlineAction = null) => {
    setConfirm(c => ({ ...c, isLoading: true }));
    try { await fn(); }
    catch (err) {
      if (!navigator.onLine && offlineAction) {
        await enqueueOfflineAction(offlineAction);
        toast.info('Operazione salvata in coda offline.');
      } else { toast.error(err.response?.data?.detail || 'Errore'); }
    } finally { setConfirm(c => ({ ...c, open: false, isLoading: false })); }
  };

  const handlePurchase = () => {
    if (!amountInput || parseFloat(amountInput) <= 0) { toast.error('Inserisci un importo valido'); return; }
    const amt = parseFloat(amountInput);
    openConfirm(
      `Aggiungi ${previewPoints} punti`,
      `Per ${customer.name} | Acquisto €${amt.toFixed(2)}`,
      (customer.points || 0) + previewPoints,
      () => runConfirm(async () => {
        const res = await staffApi.post('/transactions/purchase', { customer_id: id, amount_eur: amt });
        setCustomer(res.data.customer);
        setTransactions([]);
        setAmountInput('');
        toast.success(`+${previewPoints} punti aggiunti a ${customer.name}!`);
        if (res.data.tier_upgrade) toast.success(`Tier aggiornato a ${tierNames[res.data.customer.tier] || res.data.customer.tier}!`);
      }, { url: '/api/transactions/purchase', method: 'POST', body: { customer_id: id, amount_eur: amt }, label: `+${previewPoints}pt a ${customer.name}` })
    );
  };

  const handleRedeem = (reward) => {
    if ((customer.points || 0) < reward.points_cost) { toast.error('Punti insufficienti'); return; }
    openConfirm(
      `Riscatta ${reward.emoji} ${reward.name}`,
      `Per ${customer.name} | Costo: ${reward.points_cost} pt`,
      (customer.points || 0) - reward.points_cost,
      () => runConfirm(async () => {
        const res = await staffApi.post('/transactions/redeem', { customer_id: id, reward_id: reward.id });
        setCustomer(res.data.customer);
        setTransactions([]);
        toast.success(`${reward.emoji} ${reward.name} riscattato da ${customer.name}!`);
      }, { url: '/api/transactions/redeem', method: 'POST', body: { customer_id: id, reward_id: reward.id }, label: `Riscatto ${reward.name} per ${customer.name}` })
    );
  };

  const handleClaimBenefit = (benefit) => {
    openConfirm(
      `Ritira ${benefit.item_emoji || '🎁'} ${benefit.item_name}`,
      `Vantaggio tier ${benefit.tier} per ${customer.name}`,
      null,
      () => runConfirm(async () => {
        const res = await staffApi.post(`/tier-benefits/${benefit.id}/claim/${id}`);
        toast.success(res.data.message);
        staffApi.get(`/tier-benefits/claims/${id}`).then(r => setBenefitClaims(r.data)).catch(() => {});
      }, { url: `/api/tier-benefits/${benefit.id}/claim/${id}`, method: 'POST', body: {}, label: `Ritiro ${benefit.item_name} per ${customer.name}` })
    );
  };

  const handleRedeemCredited = (cr) => {
    openConfirm(
      `Riscatta ${cr.emoji || '🎁'} ${cr.description}`,
      `Regalo per ${customer.name} (${cr.source_detail || cr.source})`,
      null,
      () => runConfirm(async () => {
        const res = await staffApi.post(`/customers/${id}/credited-rewards/${cr.id}/redeem`);
        toast.success(res.data.message);
        setCreditedRewards(prev => prev.filter(r => r.id !== cr.id));
      }, { url: `/api/customers/${id}/credited-rewards/${cr.id}/redeem`, method: 'POST', body: {}, label: `Riscatto ${cr.description} per ${customer.name}` })
    );
  };

  const handleReferral = async () => {
    if (!refForm.name.trim()) { toast.error('Inserisci il nome'); return; }
    if (refForm.phone.length < 9) { toast.error('Inserisci un numero valido'); return; }
    setRefLoading(true);
    try {
      const body = { referrer_customer_id: id, referred_name: refForm.name.trim(), referred_phone: refForm.phone.trim() };
      if (refForm.email.trim()) body.email = refForm.email.trim();
      if (refForm.birthday) body.birthday = refForm.birthday;
      const res = await staffApi.post('/referrals/register', body);
      toast.success(res.data.message);
      setRefForm({ name: '', phone: '', email: '', birthday: '' });
      staffApi.get(`/customers/${id}/referrals`).then(r => setReferrals(r.data)).catch(() => {});
    } catch (err) {
      if (!navigator.onLine) {
        const body = { referrer_customer_id: id, referred_name: refForm.name.trim(), referred_phone: refForm.phone.trim() };
        if (refForm.email.trim()) body.email = refForm.email.trim();
        if (refForm.birthday) body.birthday = refForm.birthday;
        await enqueueOfflineAction({ url: '/api/referrals/register', method: 'POST', body, label: `Referral ${refForm.name.trim()} da ${customer.name}` });
        toast.info('Referral salvato offline.');
        setRefForm({ name: '', phone: '', email: '', birthday: '' });
      } else { toast.error(err.response?.data?.detail || 'Errore registrazione referral'); }
    } finally { setRefLoading(false); }
  };

  const handleStartOnboarding = async () => {
    setOnboardingLoading(true);
    try {
      const res = await staffApi.post(`/customers/${id}/start-onboarding`);
      // Build URL from current page origin (host-agnostic). The backend
      // also returns `consent_url` but we never trust it for display —
      // it may carry a stale preview host on production deploys.
      setOnboardingUrl(buildPublicUrl(`/consent/${res.data.consent_token}`));
      // Optimistically update the customer's consent token + expiry in
      // local state so `consentExpired` flips to false immediately and
      // the QR re-appears without waiting for the network refresh.
      setCustomer(prev => prev ? {
        ...prev,
        consent_token: res.data.consent_token,
        consent_token_expires: res.data.consent_token_expires,
        status: 'pending_consent',
      } : prev);
      // Background refresh to pull any server-side fields we may have missed.
      refresh();
      toast.success('QR onboarding generato! Fai scansionare al cliente.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setOnboardingLoading(false); }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponValidation(null);
    try {
      const res = await staffApi.post('/coupons/validate', { customer_id: id, coupon_code: couponCode });
      setCouponValidation(res.data);
    } catch (err) {
      setCouponValidation({ valid: false, error: err.response?.data?.detail || 'Codice non trovato' });
    } finally { setCouponLoading(false); }
  };

  const handleVoidTransaction = (tx) => {
    openConfirm(
      `Annulla ${TX_LABELS[tx.type] || tx.type}`,
      `${tx.points_delta > 0 ? '-' : '+'}${Math.abs(tx.points_delta)} pt verranno rimborsati a ${customer.name}`,
      Math.max(0, (customer.points || 0) - tx.points_delta),
      () => runConfirm(async () => {
        const res = await staffApi.post(`/transactions/${tx.id}/void`);
        setCustomer(res.data.customer);
        setTransactions(prev => prev.map(t => t.id === tx.id ? { ...t, voided: true } : t));
        toast.success('Transazione annullata');
      })
    );
  };

  const handleRedeemCoupon = () => {
    if (!couponValidation?.valid) return;
    const c = couponValidation.coupon;
    const typeLabels = { bonus_points: `+${c.bonus_points} punti bonus`, discount: `Sconto ${c.discount_percent}%`, prize: `Premio: ${c.prize_name}`, game_token: `+${c.tokens_amount} gettoni gioco` };
    openConfirm(
      `Riscatta coupon ${c.code}`,
      `${typeLabels[c.coupon_type] || c.coupon_type} per ${customer.name}`,
      c.coupon_type === 'bonus_points' ? (customer.points || 0) + (c.bonus_points || 0) : null,
      () => runConfirm(async () => {
        const res = await staffApi.post('/coupons/redeem', { customer_id: id, coupon_code: couponCode });
        toast.success(res.data.message);
        setCouponCode('');
        setCouponValidation(null);
        refresh();
      })
    );
  };

  const handlePurchaseTokens = () => {
    if (!tokenSettings?.tokens_enabled || tokenQty < 1) return;
    const cost = tokenQty * (tokenSettings.tokens_points_cost || 100);
    openConfirm(
      `Acquista ${tokenQty} gettoni`,
      `Costo: ${cost} punti (${tokenSettings.tokens_points_cost}pt ciascuno)`,
      (customer.points || 0) - cost,
      () => runConfirm(async () => {
        const res = await staffApi.post('/tokens/purchase', { customer_id: id, quantity: tokenQty });
        toast.success(res.data.message);
        setTokenQty(1);
        refresh();
      })
    );
  };

  if (loading) return (
    <div className="staff-app flex items-center justify-center min-h-dvh">
      <div className="w-10 h-10 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!customer) return null;

  const isPendingReferral = customer.status === 'pending_referral';
  const isPendingConsent = customer.status === 'pending_consent';
  const lifetime = customer.lifetime_points || customer.points || 0;
  const progress = getProgressToNextTier(lifetime, tierNames);

  // Consent token countdown — only relevant in pending_consent state.
  // `consent_token_expires` is an ISO timestamp 10 min after token creation.
  // Defensive parse: if the backend ever serialises a naive datetime
  // (no `+00:00` / `Z` suffix), `new Date(...)` would treat it as LOCAL
  // time on the user's browser and silently subtract the UTC offset
  // (e.g. 2h in Italy summer) → the token would appear expired
  // immediately. We force UTC interpretation when no tz info is present.
  const parseConsentExpiry = (raw) => {
    if (!raw) return null;
    if (typeof raw !== 'string') return new Date(raw).getTime();
    // ISO with explicit timezone (Z or ±HH:MM) → trust it
    if (/Z$|[+-]\d{2}:?\d{2}$/.test(raw)) return new Date(raw).getTime();
    // Naive ISO → assume UTC
    return new Date(raw + 'Z').getTime();
  };
  const consentExpiry = parseConsentExpiry(customer.consent_token_expires);
  const consentMsLeft = consentExpiry ? consentExpiry - now : null;
  const consentExpired = consentMsLeft !== null && consentMsLeft <= 0;
  const consentSoon = consentMsLeft !== null && consentMsLeft > 0 && consentMsLeft <= 60 * 1000; // <= 1 min
  const consentLabel = (() => {
    if (consentMsLeft === null) return null;
    if (consentExpired) return 'Token scaduto';
    const totalSec = Math.floor(consentMsLeft / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return m > 0 ? `Scade tra ${m}m ${s.toString().padStart(2,'0')}s` : `Scade tra ${s}s`;
  })();

  return (
    <div className="staff-app flex flex-col min-h-dvh">
      <OfflineBanner token={localStorage.getItem('staff_token')} />
      {/* ── Header ── */}
      <div className="bg-[#18181E] border-b border-white/5 px-4 pt-6 pb-4">
        <div className="flex items-start gap-3 mb-4">
          <button onClick={() => navigate(-1)} data-testid="back-btn" className="w-10 h-10 flex items-center justify-center rounded-full bg-[#222228] text-[#888] flex-shrink-0">
            <ArrowLeft size={18} />
          </button>
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${TIER_AVATAR_BG[customer.tier] || 'bg-[#333]'}`}>
            <span className="text-white font-bold text-2xl">{customer.name.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-white font-bold text-lg leading-tight">{customer.name}</h2>
              <TierBadge tier={customer.tier} />
            </div>
            <p className="text-[#888] text-sm">{customer.phone}</p>
          </div>
        </div>

        {/* Pending referral banner */}
        {(isPendingReferral || isPendingConsent) && (
          <div className="mb-3 p-3 rounded-xl border border-amber-500/30 bg-amber-500/10" data-testid="pending-banner">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={16} className="text-amber-400" />
              <p className="text-amber-300 text-sm font-semibold">
                {isPendingReferral ? 'In attesa di onboarding' : 'Onboarding in corso'}
              </p>
            </div>
            <p className="text-amber-200/70 text-xs mb-3">
              {isPendingReferral
                ? 'Questo cliente è stato presentato da un amico. Avvia l\'onboarding per fargli accettare la privacy e ricevere il messaggio di benvenuto.'
                : 'Il cliente sta completando l\'onboarding. Fagli scansionare il QR code qui sotto.'}
            </p>
            {isPendingReferral && !onboardingUrl && (
              <button onClick={handleStartOnboarding} disabled={onboardingLoading} data-testid="start-onboarding-btn"
                className="w-full h-10 bg-amber-500 text-[#111114] font-bold text-sm rounded-xl flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50">
                {onboardingLoading ? <div className="w-4 h-4 border-2 border-[#111] border-t-transparent rounded-full animate-spin" /> : <UserPlus size={14} />}
                Avvia onboarding
              </button>
            )}
            {onboardingUrl && !consentExpired && (
              <div className="bg-white rounded-xl p-3 flex flex-col items-center mt-2" data-testid="onboarding-qr">
                {isPendingConsent && consentLabel && (
                  <div
                    className={`mb-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${consentSoon ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}
                    data-testid="consent-token-countdown"
                  >
                    <Clock size={12} />
                    {consentLabel}
                  </div>
                )}
                <div className="mb-2 p-2 bg-white rounded-lg">
                  <QRCodeSVG value={onboardingUrl} size={160} level="M" includeMargin={false} />
                </div>
                <p className="text-[#111] text-xs text-center font-medium">Fai scansionare questo QR al cliente</p>
              </div>
            )}
            {isPendingConsent && (consentExpired || !onboardingUrl) && (
              <button onClick={handleStartOnboarding} disabled={onboardingLoading} data-testid="regen-consent-qr-btn"
                className="w-full h-10 bg-amber-500 text-[#111114] font-bold text-sm rounded-xl flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50">
                {onboardingLoading ? <div className="w-4 h-4 border-2 border-[#111] border-t-transparent rounded-full animate-spin" /> : <QrCode size={14} />}
                {consentExpired ? 'Token scaduto — Rigenera QR' : 'Rigenera QR consenso'}
              </button>
            )}
          </div>
        )}

        {/* Points + visits (hide for pending) */}
        {!isPendingReferral && (
          <>
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="points-display text-white" data-testid="points-balance">{customer.points}</div>
                <div className="text-[#888] text-sm mt-0.5">punti disponibili</div>
              </div>
              <div className="text-right">
                <div className="text-white font-bold text-xl tabular-nums">{customer.total_visits}</div>
                <div className="text-[#888] text-xs">visite totali</div>
              </div>
            </div>
            {progress.nextTier && (
              <div>
                <div className="flex justify-between text-xs text-[#888] mb-1">
                  <span>{Math.round(progress.progress)}% verso {progress.nextTier}</span>
                  <span className="tabular-nums">{progress.pointsNeeded} pt mancanti</span>
                </div>
                <div className="w-full h-1.5 bg-[#222228] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(progress.progress, 100)}%`, backgroundColor: primary_color }} />
                </div>
              </div>
            )}
            {expiryInfo?.expiry_enabled && (expiryInfo.expiring_soon > 0 || expiryInfo.already_expired > 0) && (
              <div className="mt-3 p-2.5 bg-[#FFAA00]/10 border border-[#FFAA00]/20 rounded-xl" data-testid="expiry-warning">
                <p className="text-[#FFAA00] text-xs font-medium">
                  {expiryInfo.expiring_soon > 0 && `${expiryInfo.expiring_soon} pt in scadenza `}
                  {expiryInfo.already_expired > 0 && `| ${expiryInfo.already_expired} pt scaduti`}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Tabs ── */}
      {!isPendingReferral && (
        <>
          <div className="flex border-b border-white/5 bg-[#18181E]">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                className={`flex-1 py-3 flex flex-col items-center gap-1 text-xs font-medium transition-all ${activeTab === tab.id ? 'border-b-2' : 'text-[#888]'}`}
                style={activeTab === tab.id ? { color: primary_color, borderColor: primary_color } : undefined}>
                <tab.icon size={16} /> {tab.label}
              </button>
            ))}
          </div>

          {/* ── Tab Content ── */}
          <div className="flex-1 overflow-y-auto">
            {/* TAB: PUNTI */}
            {activeTab === 'punti' && (
              <div className="p-4 animate-fade-in">
                <div className="bg-[#18181E] rounded-2xl p-4 border border-white/5">
                  <p className="text-[#888] text-sm mb-2">Importo acquisto</p>
                  <div className="text-center text-white text-4xl font-bold tabular-nums mb-4" data-testid="amount-display">
                    € {amountInput || '0'}
                  </div>
                  <NumPad value={amountInput} onChange={setAmountInput} maxLength={8} includeDecimal />
                  {previewPoints > 0 && (
                    <div className="mt-3 text-center">
                      <span className="font-semibold" style={{ color: secondary_color }}>+{previewPoints} punti</span>
                      <span className="text-[#888] text-sm"> ({tierMultipliers[customer.tier] || 1}x {tierNames[customer.tier] || customer.tier})</span>
                    </div>
                  )}
                  <button onClick={handlePurchase} disabled={!amountInput || parseFloat(amountInput) <= 0} data-testid="add-points-btn"
                    className="mt-4 w-full h-12 text-[#111114] font-bold text-base rounded-[14px] disabled:opacity-40 active:scale-95 transition-all"
                    style={{ backgroundColor: secondary_color }}>
                    Registra Acquisto
                  </button>
                </div>
              </div>
            )}

            {/* TAB: PREMI (improved UI) */}
            {activeTab === 'premi' && (
              <PremiFastView
                customer={customer} rewards={rewards} tierBenefits={tierBenefits}
                benefitClaims={benefitClaims} creditedRewards={creditedRewards}
                onRedeem={handleRedeem} onClaimBenefit={handleClaimBenefit}
                onRedeemCredited={handleRedeemCredited}
                primaryColor={primary_color}
              />
            )}

            {/* TAB: REFERRAL */}
            {activeTab === 'referral' && (
              <div className="p-4 space-y-4 animate-fade-in">
                <div className="bg-[#D44E20]/10 rounded-2xl p-4 border border-[#D44E20]/20">
                  <p className="text-[#D44E20] font-semibold text-sm mb-1">Presenta un amico</p>
                  <p className="text-[#D44E20]/70 text-xs">Registra l'amico. I punti vengono assegnati dopo l'onboarding e il primo acquisto.</p>
                  <div className="flex gap-4 mt-3">
                    <div className="bg-white/80 rounded-lg px-3 py-1.5">
                      <span className="text-xs text-[#888]">{customer.name}:</span>
                      <span className="text-sm font-bold text-[#3ECF8E] ml-1">+{settings?.referral_points_giver || 100} pt</span>
                    </div>
                    <div className="bg-white/80 rounded-lg px-3 py-1.5">
                      <span className="text-xs text-[#888]">Amico:</span>
                      <span className="text-sm font-bold text-[#3ECF8E] ml-1">+{(settings?.welcome_points || 50) + (settings?.referral_points_receiver || 50)} pt</span>
                    </div>
                  </div>
                </div>

                <div className="bg-[#18181E] rounded-2xl p-4 border border-white/5 space-y-3">
                  <p className="text-white font-semibold">Registra l'amico di {customer.name}</p>
                  {[
                    { key: 'name', label: 'Nome *', type: 'text', placeholder: 'Nome e cognome' },
                    { key: 'phone', label: 'Telefono *', type: 'tel', placeholder: 'Es. 3391234567', inputMode: 'tel' },
                    { key: 'email', label: 'Email (facoltativa)', type: 'email', placeholder: 'email@esempio.it' },
                    { key: 'birthday', label: 'Data di nascita (facoltativa)', type: 'date', placeholder: '' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="text-[#888] text-xs mb-1 block">{f.label}</label>
                      <input type={f.type} value={refForm[f.key]}
                        onChange={e => setRefForm(prev => ({ ...prev, [f.key]: f.key === 'phone' ? e.target.value.replace(/\D/g, '') : e.target.value }))}
                        placeholder={f.placeholder} inputMode={f.inputMode} data-testid={`ref-${f.key}-input`}
                        className="w-full h-12 bg-[#222228] text-white rounded-[14px] px-4 text-sm border border-white/10 focus:border-[#D44E20] focus:outline-none placeholder:text-[#444]" />
                    </div>
                  ))}
                  <button onClick={handleReferral} disabled={!refForm.name.trim() || refForm.phone.length < 9 || refLoading} data-testid="submit-referral-btn"
                    className="w-full h-12 text-white font-bold rounded-[14px] disabled:opacity-40 active:scale-95 transition-all flex items-center justify-center gap-2"
                    style={{ backgroundColor: primary_color }}>
                    <Share2 size={16} /> {refLoading ? 'Registrazione...' : 'Registra amico'}
                  </button>
                </div>

                {referrals.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[#888] text-sm font-medium">Amici presentati ({referrals.length})</p>
                    {referrals.map(r => (
                      <div key={r.id} className="bg-[#18181E] rounded-2xl px-4 py-3 border border-white/5 flex items-center justify-between">
                        <div>
                          <span className="text-white text-sm">{r.referred_name || r.referred_phone}</span>
                          <p className="text-[#555] text-xs mt-0.5">
                            {r.created_at ? new Date(r.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : ''}
                          </p>
                        </div>
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${r.status === 'completed' ? 'bg-[#3ECF8E]/20 text-[#3ECF8E]' : 'bg-[#FFAA00]/20 text-[#FFAA00]'}`}>
                          {r.status === 'completed' ? 'Completato' : 'In attesa'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TAB: STORICO */}
            {activeTab === 'storico' && (
              <div className="p-4 animate-fade-in">
                {transactions.length === 0 ? (
                  <div className="text-center py-12 text-[#888]"><Clock size={40} className="mx-auto mb-3 opacity-30" /><p>Nessuna transazione</p></div>
                ) : (
                  <div className="space-y-2">
                    {transactions.map(tx => {
                      const voidWindowMin = Math.max(parseInt(settings.notification_delay_minutes ?? 0) || 0, 5);
                      const ageMin = tx.created_at ? (Date.now() - new Date(tx.created_at).getTime()) / 60000 : Infinity;
                      const canVoid = !tx.voided && ['purchase', 'manual_bonus'].includes(tx.type) && ageMin <= voidWindowMin;
                      return (
                        <div key={tx.id} data-testid={`tx-row-${tx.id}`}
                          className={`bg-[#18181E] rounded-2xl px-4 py-3 border border-white/5 ${tx.voided ? 'opacity-50' : ''}`}>
                          <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-white text-sm font-medium">{TX_LABELS[tx.type] || tx.type}</p>
                                {tx.voided && <span className="text-[10px] font-bold text-[#FF5555] bg-[#FF5555]/15 px-2 py-0.5 rounded-md">ANNULLATA</span>}
                              </div>
                              {tx.note && <p className="text-[#888] text-xs truncate">{tx.note}</p>}
                              <p className="text-[#555] text-xs">{tx.created_at ? new Date(tx.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                            </div>
                            <span className={`font-bold text-lg tabular-nums ${tx.voided ? 'line-through text-[#666]' : tx.points_delta > 0 ? 'text-[#3ECF8E]' : 'text-[#FF5555]'}`}>
                              {tx.points_delta > 0 ? '+' : ''}{tx.points_delta}
                            </span>
                          </div>
                          {canVoid && (
                            <button onClick={() => handleVoidTransaction(tx)} data-testid={`void-tx-${tx.id}`}
                              className="mt-2 w-full h-9 bg-[#FF5555]/10 text-[#FF5555] border border-[#FF5555]/30 text-xs font-semibold rounded-xl flex items-center justify-center gap-1.5 active:scale-95 transition-all">
                              <Undo2 size={12} /> Annulla transazione (entro {Math.max(0, Math.ceil(voidWindowMin - ageMin))} min)
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* TAB: COUPON */}
            {activeTab === 'coupon' && (
              <div className="p-4 animate-fade-in">
                <div className="bg-[#18181E] rounded-2xl p-5 border border-white/5 mb-4">
                  <p className="text-white font-semibold mb-1">Riscatta Coupon</p>
                  <p className="text-[#888] text-xs mb-4">Inserisci il codice coupon del cliente</p>
                  <div className="flex gap-2">
                    <input data-testid="staff-coupon-input" value={couponCode}
                      onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponValidation(null); }}
                      placeholder="Es: WELCOME50"
                      className="flex-1 px-4 py-3 bg-[#222228] text-white rounded-xl text-sm font-mono border border-white/10 focus:outline-none focus:border-[#D44E20]"
                      onKeyDown={e => e.key === 'Enter' && handleValidateCoupon()} />
                    <button onClick={handleValidateCoupon} disabled={couponLoading || !couponCode.trim()} data-testid="staff-coupon-validate-btn"
                      className="px-5 py-3 bg-[#D44E20] text-white text-sm font-semibold rounded-xl disabled:opacity-40 active:scale-95 transition-all">
                      {couponLoading ? '...' : 'Verifica'}
                    </button>
                  </div>

                  {couponValidation && (
                    <div className={`mt-4 p-4 rounded-xl border ${couponValidation.valid ? 'bg-[#3ECF8E]/10 border-[#3ECF8E]/30' : 'bg-[#FF5555]/10 border-[#FF5555]/30'}`}>
                      {couponValidation.valid ? (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <Check size={16} className="text-[#3ECF8E]" />
                            <span className="text-[#3ECF8E] text-sm font-semibold">Coupon valido</span>
                          </div>
                          <p className="text-white text-sm font-mono font-bold mb-1">{couponValidation.coupon.code}</p>
                          {couponValidation.coupon.description && <p className="text-[#888] text-xs mb-2">{couponValidation.coupon.description}</p>}
                          <div className="text-xs text-[#ccc]">
                            {couponValidation.coupon.coupon_type === 'bonus_points' && <span>+{couponValidation.coupon.bonus_points} punti bonus</span>}
                            {couponValidation.coupon.coupon_type === 'discount' && <span>Sconto {couponValidation.coupon.discount_percent}%</span>}
                            {couponValidation.coupon.coupon_type === 'prize' && <span>Premio: {couponValidation.coupon.prize_name}</span>}
                            {couponValidation.coupon.coupon_type === 'game_token' && <span>+{couponValidation.coupon.tokens_amount} gettoni gioco</span>}
                          </div>
                          <button onClick={handleRedeemCoupon} data-testid="staff-coupon-redeem-btn"
                            className="w-full mt-3 py-3 bg-[#3ECF8E] text-black text-sm font-bold rounded-xl active:scale-95 transition-all">
                            Riscatta Coupon
                          </button>
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <AlertCircle size={16} className="text-[#FF5555]" />
                          <span className="text-[#FF5555] text-sm">{couponValidation.error}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Token balance + purchase */}
                <div className="bg-[#18181E] rounded-2xl p-4 border border-white/5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-[#888] text-xs uppercase font-semibold">Gettoni Gioco</p>
                      <p className="text-white text-2xl font-bold" data-testid="staff-token-balance">{customer.game_tokens || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-[#FF9800]/20 rounded-xl flex items-center justify-center">
                      <Ticket size={24} className="text-[#FF9800]" />
                    </div>
                  </div>

                  {tokenSettings?.tokens_enabled && (
                    <div className="border-t border-white/5 pt-3">
                      <p className="text-[#888] text-xs mb-2">Acquista gettoni ({tokenSettings.tokens_points_cost}pt ciascuno)</p>
                      {(() => {
                        const minTier = tokenSettings.tokens_min_tier || 'silver';
                        const tierRank = { bronze: 0, silver: 1, gold: 2, platinum: 3 };
                        const custTier = customer.tier || 'bronze';
                        if (tierRank[custTier] < tierRank[minTier]) {
                          return <p className="text-[#FF9800] text-xs">Richiesto tier <span className="capitalize font-semibold">{minTier}</span>+</p>;
                        }
                        const cost = tokenQty * (tokenSettings.tokens_points_cost || 100);
                        const canAfford = (customer.points || 0) >= cost;
                        return (
                          <div className="flex items-center gap-2">
                            <button onClick={() => setTokenQty(q => Math.max(1, q - 1))} data-testid="token-qty-minus"
                              className="w-9 h-9 bg-[#222228] text-white rounded-lg font-bold active:scale-90 transition-all">-</button>
                            <span className="text-white font-bold text-lg w-8 text-center" data-testid="token-qty-value">{tokenQty}</span>
                            <button onClick={() => setTokenQty(q => q + 1)} data-testid="token-qty-plus"
                              className="w-9 h-9 bg-[#222228] text-white rounded-lg font-bold active:scale-90 transition-all">+</button>
                            <button onClick={handlePurchaseTokens} disabled={!canAfford} data-testid="token-purchase-btn"
                              className="flex-1 py-2.5 bg-[#FF9800] text-black text-sm font-bold rounded-xl disabled:opacity-30 active:scale-95 transition-all">
                              {canAfford ? `Acquista (${cost}pt)` : `Punti insufficienti (${cost}pt)`}
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB: CAMPAGNA */}
            {activeTab === 'campagna' && (
              <div className="p-4 animate-fade-in" data-testid="tab-campagna">
                <button
                  onClick={() => setShowAssignCampaignModal(true)}
                  data-testid="open-assign-campaign-btn"
                  className="w-full py-3 mb-4 bg-[#D44E20] text-white text-sm font-bold rounded-2xl active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-40"
                  disabled={!activeCampaigns.length}
                >
                  <Megaphone size={16} />
                  {activeCampaigns.length ? 'Assegna campagna attiva' : 'Nessuna campagna attiva'}
                </button>
                {campaignPrizes.length === 0 ? (
                  <div className="text-center py-8">
                    <Megaphone size={36} className="mx-auto mb-3 text-[#333]" />
                    <p className="text-[#888] text-sm">Nessun premio campagna</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {campaignPrizes.map(p => (
                      <div key={p.id} className="bg-[#18181E] rounded-2xl p-4 border border-white/5">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-white font-semibold text-sm">{p.prize_name}</p>
                            <p className="text-[#888] text-xs">{p.campaign_name} · {p.coupon_type}</p>
                          </div>
                          {p.status === 'available' ? (
                            <span className="px-2 py-1 bg-[#3ECF8E]/20 text-[#3ECF8E] text-xs font-bold rounded-full">Disponibile</span>
                          ) : p.status === 'redeemed' ? (
                            <span className="px-2 py-1 bg-[#888]/20 text-[#888] text-xs font-bold rounded-full">Riscattato</span>
                          ) : (
                            <span className="px-2 py-1 bg-[#FF5555]/20 text-[#FF5555] text-xs font-bold rounded-full">Scaduto</span>
                          )}
                        </div>
                        <div className="text-[#555] text-xs mb-2">
                          {p.expires_at && `Scade: ${new Date(p.expires_at).toLocaleDateString('it-IT')}`}
                        </div>
                        {p.conditions_summary && (
                          <div className="mb-2 px-3 py-2 rounded-lg bg-[#D44E20]/10 border border-[#D44E20]/30" data-testid={`prize-conditions-${p.id}`}>
                            <p className="text-[#D44E20] text-[10px] font-bold uppercase tracking-wider mb-0.5">Vincoli di riscatto</p>
                            <p className="text-[#FFF] text-xs">{p.conditions_summary}</p>
                          </div>
                        )}
                        {p.status === 'available' && p.blocked_reason && (
                          <div className="mb-2 px-3 py-2 rounded-lg bg-[#FF5555]/10 border border-[#FF5555]/30" data-testid={`prize-blocked-${p.id}`}>
                            <p className="text-[#FF5555] text-xs font-medium">🔒 {p.blocked_reason}</p>
                          </div>
                        )}
                        {p.status === 'available' && (
                          <button
                            onClick={async () => {
                              if (p.blocked_reason && !window.confirm(`Attenzione: ${p.blocked_reason}\n\nVuoi provare comunque a riscattare? Il server verificherà.`)) return;
                              try {
                                const res = await staffApi.post(`/campaigns/prizes/${p.id}/redeem`);
                                toast.success(res.data.message);
                                setCampaignPrizesLoaded(false);
                                refresh();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Errore riscatto'); }
                            }}
                            data-testid={`redeem-campaign-prize-${p.id}`}
                            className={`w-full py-2.5 text-sm font-bold rounded-xl active:scale-95 transition-all ${
                              p.blocked_reason ? 'bg-[#555] text-[#aaa]' : 'bg-[#3ECF8E] text-black'
                            }`}
                          >
                            {p.blocked_reason ? 'Riscatta (vincoli attivi)' : 'Riscatta premio'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TAB: QR CODE */}
            {activeTab === 'qr' && (
              <div className="p-4 animate-fade-in">
                <div className="bg-[#18181E] rounded-2xl p-6 border border-white/5 flex flex-col items-center">
                  <p className="text-white font-semibold mb-1">QR Code di {customer.name}</p>
                  <p className="text-[#888] text-sm mb-5 text-center">Mostra al cliente per identificazione rapida</p>
                  <div className="bg-white rounded-2xl p-4 mb-5">
                    <img src={`${getBackendUrl()}/api/customers/${id}/qrcode`} alt="QR Code" data-testid="customer-qr-code" className="w-56 h-56" />
                  </div>
                  <a href={`${getBackendUrl()}/api/customers/${id}/qrcode`} download={`qr_${customer.name.replace(/\s+/g, '_')}.png`} data-testid="download-qr-btn"
                    className="w-full h-11 bg-[#222228] text-white text-sm font-semibold rounded-[14px] flex items-center justify-center gap-2 active:scale-95 transition-all">
                    <QrCode size={16} /> Scarica QR
                  </a>
                  {/* One-tap PIN recovery: re-sends the welcome SMS
                      with the customer's PIN + bacheca link. Resolves
                      forgotten-PIN issues at the till in <5s. */}
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Inviare PIN e link bacheca al numero ${customer.phone}?`)) return;
                      try {
                        const r = await staffApi.post(`/customers/${id}/resend-credentials`);
                        if (r.data.sent) {
                          toast.success(r.data.rotated_pin
                            ? 'PIN rigenerato e inviato via SMS'
                            : 'PIN inviato via SMS');
                        } else {
                          toast.error('SMS non inviato: ' + (r.data.error || 'errore Twilio'));
                        }
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Errore invio');
                      }
                    }}
                    data-testid="resend-credentials-btn"
                    className="w-full h-11 mt-3 bg-[#D44E20]/15 border border-[#D44E20]/40 text-[#D44E20] text-sm font-semibold rounded-[14px] flex items-center justify-center gap-2 active:scale-95 transition-all"
                  >
                    <Send size={16} /> Reinvia PIN e link bacheca
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmModal isOpen={confirm.open} title={confirm.title} summary={confirm.summary}
        newBalance={confirm.newBalance} onConfirm={confirm.onConfirm} onCancel={closeConfirm} isLoading={confirm.isLoading} />

      {showAssignCampaignModal && (
        <AssignCampaignModal
          campaigns={activeCampaigns}
          onClose={() => setShowAssignCampaignModal(false)}
          loading={assigningCampaign}
          onAssign={async (campaignId) => {
            setAssigningCampaign(true);
            try {
              const res = await staffApi.post(`/campaigns/${campaignId}/assign-prize`, { customer_id: id });
              if (res.data?.deferred) {
                toast.success('Campagna riservata. Premio assegnato dopo il consenso privacy.');
              } else {
                toast.success(`Premio assegnato: ${res.data?.prize_name || 'campagna'}`);
              }
              setShowAssignCampaignModal(false);
              setCampaignPrizesLoaded(false);
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Errore nell\'assegnazione della campagna');
            } finally {
              setAssigningCampaign(false);
            }
          }}
        />
      )}
    </div>
  );
}

function AssignCampaignModal({ campaigns, onClose, onAssign, loading }) {
  const [selected, setSelected] = useState(campaigns[0]?.id || '');
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="assign-campaign-modal">
      <div className="bg-[#18181E] rounded-2xl p-5 w-full max-w-sm border border-white/10" onClick={e => e.stopPropagation()}>
        <h3 className="text-white font-bold text-lg mb-1">Assegna campagna</h3>
        <p className="text-[#888] text-xs mb-4">Il cliente riceverà un premio estratto a sorte dalla campagna selezionata.</p>
        <label className="text-[#888] text-xs block mb-2">Campagna attiva</label>
        <select
          value={selected}
          onChange={e => setSelected(e.target.value)}
          data-testid="assign-campaign-select"
          className="w-full h-11 bg-[#111114] border border-white/10 rounded-xl px-3 text-white text-sm mb-5 focus:outline-none focus:border-[#D44E20]"
        >
          {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-white/10 text-white text-sm font-medium hover:bg-white/5">Annulla</button>
          <button
            onClick={() => selected && onAssign(selected)}
            disabled={!selected || loading}
            data-testid="confirm-assign-campaign-btn"
            className="flex-1 h-11 rounded-xl bg-[#D44E20] text-white text-sm font-bold disabled:opacity-50 active:scale-95 transition-all"
          >
            {loading ? 'Assegnazione...' : 'Assegna'}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── PREMI FAST VIEW (improved UI for operator speed) ── */
function PremiFastView({ customer, rewards, tierBenefits, benefitClaims, creditedRewards = [], onRedeem, onClaimBenefit, onRedeemCredited, primaryColor }) {
  const pts = customer.points || 0;
  const tierRank = TIER_RANK[customer.tier || 'bronze'] || 0;
  const now = new Date();

  // Split rewards: available NOW vs not yet
  const redeemable = [];
  const notYet = [];
  rewards.forEach(r => {
    const canRedeem = pts >= r.points_cost;
    const tierOk = tierRank >= (TIER_RANK[r.min_tier || 'bronze'] || 0);
    const hourOk = r.available_from_hour == null || (now.getHours() >= r.available_from_hour && now.getHours() < r.available_to_hour);
    const dayOk = !r.allowed_days || r.allowed_days.length === 0 || r.allowed_days.includes(now.getDay() === 0 ? 6 : now.getDay() - 1);
    if (canRedeem && tierOk && hourOk && dayOk) redeemable.push(r);
    else notYet.push({ ...r, reason: !tierOk ? `Tier ${r.min_tier}` : !canRedeem ? `Mancano ${r.points_cost - pts} pt` : !hourOk ? 'Fuori orario' : 'Non oggi' });
  });

  // Tier benefits available
  const availBenefits = tierBenefits.filter(b => b.is_active && tierRank >= (TIER_RANK[b.tier] || 0));

  return (
    <div className="p-4 space-y-4 animate-fade-in">
      {/* SECTION 0: Credited Rewards (gifts to redeem) */}
      {creditedRewards.length > 0 && (
        <div data-testid="credited-rewards-section">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-[#FFAA00] animate-pulse" />
            <p className="text-[#FFAA00] text-xs font-bold uppercase tracking-wider">Regali da ritirare</p>
          </div>
          <div className="space-y-2">
            {creditedRewards.map(cr => {
              const daysLeft = cr.expires_at ? Math.max(0, Math.ceil((new Date(cr.expires_at) - now) / (1000 * 60 * 60 * 24))) : null;
              const sourceLabels = { birthday: 'Compleanno', second_visit_month: '2a visita', tenth_visit_year: '10a visita', challenge: 'Challenge', milestone_second_visit: '2a visita', milestone_tenth_visit: '10a visita' };
              const conditionsLabel = formatRedemptionConditionsShort(cr.redemption_conditions);
              const redeemableNow = isRedeemableNow(cr.redemption_conditions, customer);
              return (
                <div key={cr.id} data-testid={`credited-${cr.id}`}
                  className="bg-[#FFAA00]/10 rounded-2xl p-4 border border-[#FFAA00]/30">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{cr.emoji || '🎁'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">{cr.description}</p>
                      <p className="text-[#FFAA00]/70 text-xs">{sourceLabels[cr.source] || cr.source_detail}</p>
                      {daysLeft !== null && (
                        <p className={`text-xs mt-0.5 ${daysLeft <= 3 ? 'text-red-400' : 'text-[#888]'}`}>
                          {daysLeft <= 0 ? 'Scade oggi!' : `Scade tra ${daysLeft}g`}
                        </p>
                      )}
                    </div>
                    <button onClick={() => onRedeemCredited(cr)} data-testid={`redeem-credited-${cr.id}`}
                      className="px-4 py-2.5 bg-[#FFAA00] text-[#111114] text-sm font-bold rounded-xl active:scale-95 transition-all flex items-center gap-1.5">
                      <Check size={14} /> Ritira
                    </button>
                  </div>
                  {conditionsLabel && (
                    <div
                      className={`mt-2.5 text-[11px] font-medium px-3 py-1.5 rounded-lg flex items-center gap-1.5 ${redeemableNow ? 'bg-white/5 text-[#FFAA00]/90' : 'bg-red-500/15 text-red-300'}`}
                      data-testid={`credited-conditions-${cr.id}`}
                    >
                      <Clock size={11} />
                      <span className="truncate">{conditionsLabel}</span>
                      {!redeemableNow && <span className="ml-auto whitespace-nowrap font-bold">❌ Non oggi</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SECTION 1: Redeemable NOW */}
      {redeemable.length > 0 && (
        <div data-testid="redeemable-section">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-[#3ECF8E] animate-pulse" />
            <p className="text-[#3ECF8E] text-xs font-bold uppercase tracking-wider">Pronto da ritirare</p>
          </div>
          <div className="space-y-2">
            {redeemable.map(r => (
              <div key={r.id} data-testid={`reward-ready-${r.id}`}
                className="bg-[#3ECF8E]/10 rounded-2xl p-4 border border-[#3ECF8E]/30 flex items-center gap-3">
                <span className="text-3xl">{r.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm">{r.name}</p>
                  <p className="text-[#3ECF8E] text-xs">{r.points_cost} punti</p>
                </div>
                <button onClick={() => onRedeem(r)} data-testid={`redeem-${r.id}`}
                  className="px-4 py-2.5 bg-[#3ECF8E] text-[#111114] text-sm font-bold rounded-xl active:scale-95 transition-all flex items-center gap-1.5">
                  <Check size={14} /> Riscatta
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SECTION 2: Tier Benefits */}
      {availBenefits.length > 0 && (
        <div data-testid="tier-benefits-section">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: TIER_COLORS[customer.tier] || '#CD7F32' }} />
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: TIER_COLORS[customer.tier] || '#CD7F32' }}>
              Vantaggi {TIER_LABELS[customer.tier] || customer.tier}
            </p>
          </div>
          <div className="space-y-2">
            {availBenefits.map(b => {
              const claim = benefitClaims[b.id];
              const isFreeItem = b.benefit_type === 'free_item';
              const allClaimed = isFreeItem && claim && claim.claimed >= claim.max;
              const tierColor = TIER_COLORS[b.tier] || '#CD7F32';
              return (
                <div key={b.id} data-testid={`tier-benefit-${b.id}`}
                  className={`rounded-2xl p-4 border flex items-center gap-3 ${allClaimed ? 'bg-[#18181E] border-white/5 opacity-50' : 'bg-[#18181E] border-white/10'}`}
                  style={!allClaimed ? { borderColor: `${tierColor}40`, backgroundColor: `${tierColor}08` } : undefined}>
                  <span className="text-2xl">{b.benefit_type === 'discount' ? '🏷️' : (b.item_emoji || '🎁')}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm">
                      {b.benefit_type === 'discount' ? `Sconto ${b.discount_pct}%` : b.item_name}
                    </p>
                    {b.description && <p className="text-[#888] text-xs">{b.description}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${tierColor}20`, color: tierColor }}>{b.tier}</span>
                      {isFreeItem && b.frequency && <span className="text-[#888] text-xs">{b.frequency}</span>}
                      {isFreeItem && claim && <span className="text-[#888] text-xs">({claim.claimed}/{claim.max})</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {isFreeItem && !allClaimed && (
                      <button onClick={() => onClaimBenefit(b)} data-testid={`claim-benefit-${b.id}`}
                        className="px-3 py-2 text-[#111114] text-xs font-bold rounded-xl active:scale-95 transition-all" style={{ backgroundColor: tierColor }}>
                        Ritira
                      </button>
                    )}
                    {isFreeItem && allClaimed && <span className="text-[#555] text-xs">Ritirato</span>}
                    {b.benefit_type === 'discount' && (
                      <span className="text-xs font-bold px-2 py-1 rounded-lg" style={{ color: tierColor, backgroundColor: `${tierColor}15` }}>Attivo</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SECTION 3: Not yet redeemable */}
      {notYet.length > 0 && (
        <div data-testid="not-redeemable-section">
          <p className="text-[#555] text-xs font-bold uppercase tracking-wider mb-2">Non ancora disponibili</p>
          <div className="space-y-2">
            {notYet.map(r => (
              <div key={r.id} data-testid={`reward-locked-${r.id}`}
                className="bg-[#18181E] rounded-2xl p-3.5 border border-white/5 flex items-center gap-3 opacity-50">
                <span className="text-2xl grayscale">{r.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-[#888] font-medium text-sm">{r.name}</p>
                  <p className="text-[#555] text-xs">{r.points_cost} pt — {r.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {rewards.length === 0 && availBenefits.length === 0 && (
        <div className="text-center py-12 text-[#888]">
          <Gift size={40} className="mx-auto mb-3 opacity-30" />
          <p>Caricamento premi...</p>
        </div>
      )}
    </div>
  );
}
